import React, { useEffect, useState } from "react";
import { Box, Typography, Button, Grid, Select, MenuItem } from "@mui/material";
import { Target, LayersThree } from "@mynaui/icons-react";
import KpiCard from "../components/KpiCard";
import RepairCostsByCategory from "../components/RepairCostsByCategory";
import AiDrivenInsights from "../components/AiDrivenInsights";
import AssetsAtRisk from "../components/AssetsAtRisk";
import OptimizationGoalsModal from "../components/OptimizationGoalsModal";
import Footer from "../../../shared/components/Footer";
import { useZone } from "../../../shared/context/ZoneContext";
import dashboardService from "../../../services/dashboardService";

const KPI_KEYS = [
  "workOrders",
  "assetsAtRisk",
  "averageMttr",
  "serviceCosts",
  "averageRul",
  "outstandingWorkOrders",
];

const TIME_WINDOW_OPTIONS = [
  { value: "current_month", label: "Current Month" },
  { value: "current_quarter", label: "Current Quarter" },
];

const pad = (n) => String(n).padStart(2, "0");

const getDateRange = (window) => {
  const now = new Date();
  const end = `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`;
  const past = new Date(now);
  past.setDate(past.getDate() - (window === "current_quarter" ? 90 : 30));
  const start = `${past.getFullYear()}-${pad(past.getMonth() + 1)}-${pad(past.getDate())}`;
  return { startDate: start, endDate: end };
};

const DashboardPage = () => {
  const [optimizationOpen, setOptimizationOpen] = useState(false);
  const { selectedZone, setLastSync, selectedPlantId, isReady, locations } =
    useZone();
  const [overviewData, setOverviewData] = useState(null);
  const [overviewLoading, setOverviewLoading] = useState(true);
  const [serviceCostsDataByZone, setServiceCostsDataByZone] = useState(null);
  const [serviceCostsLoading, setServiceCostsLoading] = useState(true);
  const [assets, setAssets] = useState([]);
  const [aiInsights, setAiInsights] = useState([]);

  console.log("Locations:", selectedZone, " - ", selectedPlantId);
  const [timeWindow, setTimeWindow] = useState("current_quarter");

  const { startDate, endDate } = getDateRange(timeWindow);

  const handleOptimizationSave = (settings) => {
    console.log("Optimization settings saved:", settings);
  };

  // Fetch KPI overview
  useEffect(() => {
    if (!isReady || !selectedZone) return;
    setOverviewLoading(true);
    setOverviewData(null);
    dashboardService
      .getDashboardOverview(selectedZone, startDate, endDate)
      .then((res) => {
        setOverviewData(res.data.data);
        if (res.data.data?.lastSync) setLastSync(res.data.data.lastSync);
      })
      .catch((err) => console.error("Failed to fetch overview:", err.message))
      .finally(() => setOverviewLoading(false));
  }, [selectedZone, startDate, endDate, setLastSync, isReady]);

  // Fetch service costs by category
  const getRiskColor = (riskLevel) => {
    switch (riskLevel) {
      case "Critical":
        return "#EF4444";
      case "Medium":
        return "#F59E0B";
      case "Low":
        return "#22C55E";
      default:
        return "#0F172B";
    }
  };

  const formatRulDays = (rulDays) => {
    if (rulDays == null) return "N/A";
    if (rulDays >= 30) {
      const months = Math.floor(rulDays / 30);
      const days = rulDays % 30;
      return days > 0
        ? `${months} Month${months > 1 ? "s" : ""} ${days} days`
        : `${months} Month${months > 1 ? "s" : ""}`;
    }
    return `${rulDays} days`;
  };

  const formatLastChecked = (dateStr) => {
    if (!dateStr) return "N/A";
    const diff = Date.now() - new Date(dateStr).getTime();
    const mins = Math.floor(diff / 60000);
    if (mins < 60) return `${mins} mins ago`;
    const hours = Math.floor(mins / 60);
    if (hours < 24) return `${hours} hours ago`;
    return `${Math.floor(hours / 24)} days ago`;
  };

  const transformAsset = (dto) => ({
    id: dto.id,
    name: dto.name,
    image: dto.imageUrl,
    issue: dto.latestAlertMessage || "No issues reported",
    status: dto.status || "Unknown",
    riskLevel: dto.riskLevel || "Low",
    riskColor: getRiskColor(dto.riskLevel),
    rul: { value: formatRulDays(dto.rulDays) },
    rulColor: getRiskColor(dto.riskLevel),
    failuresLastMonth: dto.failuresLastMonth ?? 0,
    lastChecked: formatLastChecked(dto.lastCheckedAt),
  });

  useEffect(() => {
    if (!isReady || !selectedZone || !locations.length) return;

    const areas = locations.flatMap((p) => p.areas) || [];
    const area = areas.find((a) => a.code === selectedZone);
    if (!area) return;

    dashboardService
      .getAssets(area.code, "", ["critical", "high"])
      .then((res) => {
        const transformed = (res.data.data || []).map(transformAsset);
        setAssets(transformed);
      })
      .catch((err) => console.error("Failed to fetch assets:", err.message));
  }, [selectedZone, locations, isReady]);

  useEffect(() => {
    if (!isReady || !selectedZone) return;

    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(today.getDate() - 1);

    const fmt = (d) =>
      `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;

    dashboardService
      .getAiInsights(selectedZone, fmt(yesterday), fmt(today))
      .then((res) => {
        const transformed = (res.data.data || [])
        .filter((item) => item.messageHeader && item.messageHeader.trim() !== "")
        .map((item) => ({
          id: item.id,
          title: item.messageHeader,
          severity: item.priorityLabel,
          description: item.messageDetail,
          recommendation: item.notes,
          asset: item.assetName,
          assetId: item.assetId,
        }));
        setAiInsights(transformed);
      })
      .catch((err) =>
        console.error("Failed to fetch AI insights:", err.message),
      );
  }, [selectedZone, isReady]);

  useEffect(() => {
    if (!isReady || !selectedZone) return;
    setServiceCostsLoading(true);
    setServiceCostsDataByZone(null);
    dashboardService
      .getServiceCostByCategory(selectedZone, startDate, endDate)
      .then((res) => {
        console.log("Service costs by category:", res.data);
        setServiceCostsDataByZone(res.data.data);
      })
      .catch((err) =>
        console.error("Failed to fetch service costs:", err.message),
      )
      .finally(() => setServiceCostsLoading(false));
  }, [selectedZone, startDate, endDate, isReady]);

  return (
    <Box sx={{ minHeight: "100%", display: "flex", flexDirection: "column" }}>
      <Box sx={{ px: { xs: 2, md: 4 }, py: 3, flexGrow: 1 }}>
        {/* Header Row */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 3,
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <LayersThree size={22} color="#020618" strokeWidth={1.5} />
            <Typography
              sx={{
                fontWeight: 500,
                fontSize: "20px",
                lineHeight: "28px",
                color: "#020618",
              }}
            >
              Overview
            </Typography>
          </Box>

          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
            <Button
              size="small"
              onClick={() => setOptimizationOpen(true)}
              sx={{
                bgcolor: "#00A63E",
                color: "#fff",
                fontWeight: 600,
                fontSize: "0.8125rem",
                px: 2.5,
                py: 0.75,
                borderRadius: "8px",
                "&:hover": { bgcolor: "#00A63E" },
              }}
              startIcon={<Target size={16} />}
            >
              Optimization Goals
            </Button>
            <Select
              value={timeWindow}
              onChange={(e) => setTimeWindow(e.target.value)}
              size="small"
              sx={{
                height: 36,
                bgcolor: "#FFFFFF",
                border: "1px solid #E2E8F0",
                borderRadius: "6px",
                "& .MuiOutlinedInput-notchedOutline": { border: "none" },
                "& .MuiSelect-select": {
                  py: "6px",
                  px: "12px",
                  fontSize: "13px",
                  fontWeight: 500,
                  lineHeight: "20px",
                  color: "#0F172B",
                },
                "& .MuiSelect-icon": {
                  color: "#6B7280",
                  right: 8,
                  fontSize: "15px",
                },
              }}
              MenuProps={{
                PaperProps: {
                  sx: {
                    "& .MuiMenuItem-root": { fontSize: "14px" },
                  },
                },
              }}
            >
              {TIME_WINDOW_OPTIONS.map((opt) => (
                <MenuItem key={opt.value} value={opt.value}>
                  {opt.label}
                </MenuItem>
              ))}
            </Select>
          </Box>
        </Box>

        {/* KPI Cards - Row 1 */}
        <Grid container spacing={2.5} sx={{ mb: 2.5 }}>
          {KPI_KEYS.slice(0, 3).map((key) => (
            <Grid size={{ xs: 12, md: 4 }} key={key}>
              <KpiCard
                id={key}
                data={overviewData?.[key]}
                loading={overviewLoading}
              />
            </Grid>
          ))}
        </Grid>

        {/* KPI Cards - Row 2 */}
        <Grid container spacing={2.5} sx={{ mb: 3 }}>
          {KPI_KEYS.slice(3, 6).map((key) => (
            <Grid size={{ xs: 12, md: 4 }} key={key}>
              <KpiCard
                id={key}
                data={overviewData?.[key]}
                loading={overviewLoading}
              />
            </Grid>
          ))}
        </Grid>

        {/* Middle Section: Repair Costs + AI Insights */}
        <Grid container spacing={2.5} sx={{ mb: 4 }} alignItems="flex-start">
          <Grid size={{ xs: 12, md: 6 }}>
            <RepairCostsByCategory
              data={serviceCostsDataByZone}
              loading={serviceCostsLoading}
              fixedHeight={410}
            />
          </Grid>
          <Grid size={{ xs: 12, md: 6 }}>
            <AiDrivenInsights insights={aiInsights} maxHeight="410px" />
          </Grid>
        </Grid>

        {/* Assets at Risk */}
        {assets.length > 0 && <AssetsAtRisk assets={assets} />}
      </Box>

      {/* Footer */}
      <Footer />

      {/* Optimization Goals Modal */}
      <OptimizationGoalsModal
        open={optimizationOpen}
        onClose={() => setOptimizationOpen(false)}
        onSave={handleOptimizationSave}
        plantId={selectedPlantId}
      />
    </Box>
  );
};

export default DashboardPage;
